import React from "react";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { CheckboxIcon, VerticalDotsIcon } from "@aureskonnect/react-ui";
import { useSnapshot } from "valtio";
import classnames from "classnames";
import { useTranslation } from "react-i18next/";
import { uuid } from "uuidv4";

import { store } from "@components/VerticalLayout/store";
import {
    store as moneticsStore,
    setIsMultiDeletePeripheralModalOpened,
    setIsActionsClicked,
    setIsEdited,
    setActions,
    setIsLoading,
    setKey,
} from "./store";
import {
    setFN,
    setMessage,
} from "@components/Common/ConfirmationMultiDeleteMessage/store";

import ErrorToast from "@components/Common/ErrorTost";
import { useFetchGlobal } from "../../../api/hooks";
import {
    TYPE_DISPLAY_NOT_ARCHIVED,
    buildArchiveBatchPayload,
    mapArchiveBatchResult,
} from "./moneticsArchiveBatch";
import type {
    ArchiveBatchResponse,
    SelectedMonetic,
} from "./moneticsArchiveBatch";

const TOAST_OPTIONS = {
    position: toast.POSITION.TOP_CENTER,
    autoClose: 2000,
    theme: "colored" as const,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
};

type actionType = {
    typeDisplay: string;
    setTypeDisplay: Function;
    selectedIds: any;
    ListNumberOrigin: any;
    setDataIsUpdated: Function;
    selectedRows: any;
    setIsHistoryModalOpened: Function;
    setIsAddMoneticsClicked: Function;
};
export default function DropdownAction({
    typeDisplay,
    setTypeDisplay,
    selectedIds,
    ListNumberOrigin,
    setDataIsUpdated,
    selectedRows,
    setIsHistoryModalOpened,
    setIsAddMoneticsClicked,
}: actionType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { isActionsClicked, isEdited, modify, isLoading } = useSnapshot(
        moneticsStore
    );

    const { operatorID } = useSnapshot(store);
    const uuidUser = localStorage.getItem("uuidUser");

    const isBulkActionDisabled =
        isLoading || selectedIds === undefined || selectedIds.length === 0;

    const runBulkArchive = async () => {
        if (isBulkActionDisabled) return;
        setIsLoading(true);
        if (selectedIds.length === 1) {
            ErrorToast(
                `${t(
                    "Attention, it is a multiple action. Please select at least two items"
                )!}`
            );
            setIsLoading(false);
            return;
        }

        const isArchived = typeDisplay === TYPE_DISPLAY_NOT_ARCHIVED;
        const link = `${process.env.REACT_APP_API_V2_URL}/settings/monetics/archive-batch`;

        // The backend owns the "a monetic associated with an application cannot
        // be archived" rule: send the whole selection and read each item's
        // outcome from the succeeded[] / failed[] arrays of the response.
        setFN(async () => {
            setIsLoading(true);
            try {
                const result = await fetchGlobal<ArchiveBatchResponse>(link, {
                    headers: { "Content-Type": "application/json" },
                    method: "POST",
                    body: JSON.stringify(
                        buildArchiveBatchPayload(
                            selectedIds as SelectedMonetic[],
                            { uuidUser, isArchived, operatorId: operatorID }
                        )
                    ),
                });
                mutate(link, result);

                if (result.error) {
                    throw Error("Error!");
                }

                const {
                    archived,
                    associatedSkipped,
                    otherFailures,
                    malformed,
                } = mapArchiveBatchResult(result);

                // Contract deviation (no succeeded[]/failed[]) → surface an error
                // instead of failing silently.
                if (malformed) {
                    throw Error("Malformed archive-batch response");
                }

                setDataIsUpdated(true);
                setIsLoading(false);
                setKey(uuid());

                if (archived > 0) {
                    toast.success(
                        `${t(
                            isArchived
                                ? "The device was successfully archived"
                                : "The device was successfully unarchived"
                        )!}`,
                        {
                            ...TOAST_OPTIONS,
                            icon: (
                                <CheckboxIcon
                                    height={25}
                                    width={25}
                                    fill="white"
                                />
                            ),
                        }
                    );
                }

                if (associatedSkipped > 0) {
                    ErrorToast(
                        t(
                            "Please note that you cannot archive a device associated with an application."
                        )
                    );
                }

                if (otherFailures > 0) {
                    toast.error(`${t("There's an error")!}`, TOAST_OPTIONS);
                }
            } catch {
                setIsLoading(false);
                toast.error(`${t("There's an error")!}`, TOAST_OPTIONS);
            }
        });
        setMessage(
            t(
                isArchived
                    ? "Please confirm archivation of this device"
                    : "Please confirm unarchivation of this device"
            )
        );
        setIsLoading(false);
        setIsMultiDeletePeripheralModalOpened(true);
    };

    return (
        <React.Fragment>
            <div>
                <div
                    className={` cmn_drp_FRmenu ${classnames("dropdown", {})}`}
                    onClick={() => {}}
                >
                    <VerticalDotsIcon
                        id="dropdownMenuButton1"
                        data-bs-toggle="dropdown"
                        role="button"
                        tabIndex={0}
                        aria-haspopup="menu"
                        aria-label={t("Actions")!}
                        style={{ cursor: "pointer" }}
                        height={25}
                        width={25}
                        fill="black"
                        className="cmn_drp_FRmenu"
                    />
                    <div
                        className="dropdown-menu"
                        aria-labelledby="dropdownMenuButton"
                    >
                        <div
                            className="dropdown-item pl-1 cmn_btn_FRlistActivated "
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                if (isEdited === true) {
                                    setIsActionsClicked(!isActionsClicked);
                                    setActions(() => {
                                        setIsEdited(false);
                                        setTypeDisplay(
                                            typeDisplay ===
                                                TYPE_DISPLAY_NOT_ARCHIVED
                                                ? "archived"
                                                : TYPE_DISPLAY_NOT_ARCHIVED
                                        );
                                        if (
                                            typeDisplay ===
                                            TYPE_DISPLAY_NOT_ARCHIVED
                                        ) {
                                            setIsAddMoneticsClicked(false);
                                        }
                                    });
                                } else {
                                    setTypeDisplay(
                                        typeDisplay === TYPE_DISPLAY_NOT_ARCHIVED
                                            ? "archived"
                                            : TYPE_DISPLAY_NOT_ARCHIVED
                                    );
                                    if (
                                        typeDisplay === TYPE_DISPLAY_NOT_ARCHIVED
                                    ) {
                                        setIsAddMoneticsClicked(false);
                                    }
                                }
                            }}
                        >
                            <span className="ml-2">
                                {t(
                                    typeDisplay === TYPE_DISPLAY_NOT_ARCHIVED
                                        ? "Archived(s) monetic(s)"
                                        : "Activated(s) monetic(s)"
                                )}
                                (
                                {typeDisplay === TYPE_DISPLAY_NOT_ARCHIVED
                                    ? ListNumberOrigin.nbrArchived
                                    : ListNumberOrigin.nbrNotArchived}
                                )
                            </span>
                        </div>
                        {modify && (
                            <div
                                data-testid="monetics-bulk-archive-btn"
                                role="button"
                                tabIndex={0}
                                aria-disabled={isBulkActionDisabled}
                                style={{ cursor: "pointer" }}
                                className={`cmn_btn_FRarchived cmn_btn_FRunarchive cmn_btn_FRarchived ${classnames(
                                    " dropdown-item pl-1 pointer__clz",
                                    {
                                        "not-allowed-icon__clz": isBulkActionDisabled,
                                    }
                                )}`}
                                onClick={runBulkArchive}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        runBulkArchive();
                                    }
                                }}
                            >
                                <span className="ml-2">
                                    {typeDisplay === TYPE_DISPLAY_NOT_ARCHIVED
                                        ? t("Archive")
                                        : t("Dearchive")}
                                </span>
                            </div>
                        )}
                        <div
                            style={{ cursor: "pointer" }}
                            className={`${classnames(
                                " dropdown-item pl-1 pointer__clz cmn_btn_FRhistory cmn_btn_FRhistory",
                                {
                                    "not-allowed-icon__clz":
                                        selectedRows.length === 0,
                                }
                            )}`}
                            onClick={() => {
                                if (selectedRows.length === 1) {
                                    ErrorToast(
                                        `${t(
                                            "Attention, it is a multiple action. Please select at least two items"
                                        )!}`
                                    );
                                } else {
                                    setIsHistoryModalOpened(true);
                                }
                            }}
                        >
                            <span className="ml-2 cmn_btn_FRhistory">
                                {t("Historical")}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}
